import React from "react";
import { Input, DatePicker, Button, SelectItem } from "@heroui/react";
import AppSelect from "../common/appSelect";
import { ArrowLeftIcon, ArrowRightIcon } from "@heroicons/react/24/outline";
import { parseDate, today } from "@internationalized/date";
import { TFunction } from "react-i18next";
import { State, ValidationErrors } from "../../types/types";
import hoowlrLogo from "../../assets/images/logo.svg";
import loginBanner from "../../assets/images/login-banner.png";

const safeParseDate = (value?: string | null) => {
  if (!value) return null;

  const datePart = value.split("T")[0];

  if (!/^\d{4}-\d{2}-\d{2}$/.test(datePart)) {
    return null;
  }

  try {
    return parseDate(datePart);
  } catch {
    return null;
  }
};

interface PersonalDetailsStageProps {
  t: TFunction<"signUpSetupStages", undefined>;
  state: State;
  validationErrors: ValidationErrors;
  handleFormChange: (
    value: any,
    field: string | any,
    dto?: string | any
  ) => void;
  handleNext: (e: React.FormEvent | any, nextStage: string | any) => void;
  handleBack: () => void;
}

/**
 * UI tokens (Figma 1920x1080 = visual reference only)
 * - Font sizes are fixed (36 / 16 / 14 / 12). Width shrinks -> banner hides below `lg`.
 * - Height shrinks -> only padding / gaps reduce. Still no room -> page scrolls.
 */
const labelClass =
  "!text-slate-600 lg:!text-[#71717A] data-[invalid=true]:!text-[#71717A] group-data-[invalid=true]:!text-[#71717A] text-xs lg:text-sm font-semibold lg:font-medium leading-4 lg:leading-5 text-left self-start pb-0 after:hidden";

const inputClassNames = {
  base: "gap-1",
  label: labelClass,
  inputWrapper:
    "h-10 lg:h-9 min-h-10 lg:min-h-9 rounded-xl border border-slate-200 lg:border-[#EBEBEC] bg-white lg:bg-[#EBEBEC] px-3.5 lg:px-3 shadow-[0_1px_2px_rgba(0,0,0,0.03)] lg:shadow-none data-[hover=true]:border-slate-300 lg:data-[hover=true]:bg-[#EBEBEC] group-data-[focus=true]:bg-white lg:group-data-[focus=true]:bg-[#EBEBEC] group-data-[focus=true]:border-[#2563EB]",
  input: "text-xs sm:text-sm leading-5 placeholder:text-slate-400 lg:placeholder:text-[#71717A]",
  errorMessage: "text-left ml-1 w-full text-xs",
};

const selectClassNames = {
  base: "gap-1",
  label: labelClass,
  trigger:
    "h-10 lg:h-9 min-h-10 lg:min-h-9 rounded-xl border border-slate-200 lg:border-[#EBEBEC] bg-white lg:bg-[#EBEBEC] px-3.5 lg:px-3 shadow-[0_1px_2px_rgba(0,0,0,0.03)] lg:shadow-none data-[hover=true]:border-slate-300 lg:data-[hover=true]:bg-[#EBEBEC] data-[focus=true]:border-[#2563EB] data-[open=true]:border-[#2563EB]",
  value:
    "text-xs sm:text-sm leading-5 text-[#18181B] group-data-[has-value=false]:text-slate-400 lg:group-data-[has-value=false]:text-[#71717A]",
  selectorIcon: "text-[#71717A]",
  errorMessage: "text-left ml-1 w-full text-xs",
};

const datePickerClassNames = {
  base: "gap-1",
  label: labelClass,
  inputWrapper:
    "h-10 lg:h-9 min-h-10 lg:min-h-9 rounded-xl border border-slate-200 lg:border-[#EBEBEC] bg-white lg:bg-[#EBEBEC] px-3.5 lg:px-3 shadow-[0_1px_2px_rgba(0,0,0,0.03)] lg:shadow-none data-[hover=true]:border-slate-300 lg:data-[hover=true]:bg-[#EBEBEC] group-data-[focus=true]:bg-white lg:group-data-[focus=true]:bg-[#EBEBEC] group-data-[focus=true]:border-[#2563EB] group-data-[open=true]:border-[#2563EB]",
  innerWrapper: "flex items-center",
  input: "text-xs sm:text-sm leading-5",
  segment: "text-xs sm:text-sm",
  errorMessage: "text-left ml-1 w-full text-xs",
};

const tertiaryButtonClass =
  "h-10 rounded-2xl lg:rounded-3xl bg-[#EBEBEC] px-4 text-xs sm:text-sm lg:text-base font-semibold lg:font-medium leading-5 lg:leading-6 text-[#18181B] hover:bg-[#dcdcde] active:scale-[0.98] transition-all";

const PersonalDetailsStage: React.FC<PersonalDetailsStageProps> = ({
  t,
  state,
  validationErrors,
  handleFormChange,
  handleNext,
  handleBack,
}) => {
  const isFighter = state.formData.profileType === "FIGHTER";
  const dtoKey = isFighter ? "fighterDto" : "managerDto";

  const firstNameError =
    validationErrors[isFighter ? "fighterDto.firstName" : "managerDto.firstName"];

  const lastNameError =
    validationErrors[isFighter ? "fighterDto.lastName" : "managerDto.lastName"];

  const genderError = validationErrors["gender"];
  const dobError = validationErrors["fighterDto.dateOfBirth"];
  const organizationNameError = validationErrors["managerDto.organizationName"];

  return (
    <div className="flex min-h-dvh w-full bg-white font-['Plus_Jakarta_Sans',sans-serif] text-[#18181B]">
      {/* LEFT COLUMN */}
      <div className="flex min-w-0 flex-1 flex-col">
        <div
          className="
            flex min-h-dvh flex-col justify-between
            px-6 py-6 sm:px-12 sm:py-8
            lg:py-9
            [@media(min-height:701px)_and_(max-height:800px)]:lg:py-6
            [@media(max-height:700px)]:lg:py-3
          "
        >
          {/* Header: logo */}
          <div className="flex w-full items-center justify-between">
            <img
              src={hoowlrLogo}
              alt="Hoowlr"
              className="
                h-9 w-auto object-contain object-left
                lg:h-16 lg:w-48
                [@media(min-height:701px)_and_(max-height:800px)]:lg:h-14
                [@media(max-height:700px)]:lg:h-10
              "
            />
          </div>

          {/* Centered content */}
          <div className="flex flex-1 flex-col items-center justify-center my-auto w-full py-2">
            <div className="flex w-full flex-col items-center gap-1 sm:gap-2 text-center mb-4 sm:mb-6 lg:mb-8 [@media(max-height:700px)]:mb-2">
              <h1 className="text-xl sm:text-2xl lg:text-4xl font-bold lg:font-extrabold leading-tight lg:leading-10 text-[#18181B]">
                {t("personalDetails.title")}
              </h1>
              <p className="text-xs sm:text-sm lg:text-base leading-4 lg:leading-6 text-[#71717A]">
                {t("personalDetails.subtitle")}
              </p>
            </div>

            <form
              noValidate
              onSubmit={(e) => handleNext(e, "locationDetails")}
              className="
                flex w-full max-w-96 flex-col gap-4 sm:gap-5 lg:gap-6
                [@media(min-height:701px)_and_(max-height:800px)]:gap-4 lg:[@media(min-height:701px)_and_(max-height:800px)]:gap-5
                [@media(max-height:700px)]:gap-3 lg:[@media(max-height:700px)]:gap-4
              "
            >
              <div
                className="
                  flex flex-col gap-3 lg:gap-4
                  [@media(max-height:700px)]:gap-2.5 lg:[@media(max-height:700px)]:gap-3
                "
              >
            <Input
              type="text"
              name="firstName"
              label={t("personalDetails.firstName")}
              labelPlacement="outside"
              placeholder={t("personalDetails.firstNamePlaceholder")}
              maxLength={30}
              value={
                state.formData.fighterDto?.firstName ??
                state.formData.managerDto?.firstName ??
                ""
              }
              onValueChange={(value) =>
                handleFormChange(value, "firstName", dtoKey)
              }
              isRequired
              isInvalid={
                Boolean(firstNameError) &&
                !(
                  state.formData.fighterDto?.firstName ||
                  state.formData.managerDto?.firstName
                )
              }
              errorMessage={firstNameError}
              className="w-full"
              classNames={inputClassNames}
            />

            <Input
              type="text"
              name="lastName"
              label={t("personalDetails.lastName")}
              labelPlacement="outside"
              placeholder={t("personalDetails.lastNamePlaceholder")}
              maxLength={25}
              value={
                state.formData.fighterDto?.lastName ??
                state.formData.managerDto?.lastName ??
                ""
              }
              onValueChange={(value) =>
                handleFormChange(value, "lastName", dtoKey)
              }
              isRequired
              isInvalid={
                Boolean(lastNameError) &&
                !(
                  state.formData.fighterDto?.lastName ||
                  state.formData.managerDto?.lastName
                )
              }
              errorMessage={lastNameError}
              className="w-full"
              classNames={inputClassNames}
            />

            {state.formData.profileType === "ORGANIZATION" && (
              <Input
                type="text"
                name="organizationName"
                label={t("managerForm.organizationName")}
                labelPlacement="outside"
                placeholder={t("managerForm.organizationNamePlaceholder")}
                maxLength={40}
                value={state.formData.managerDto?.organizationName ?? ""}
                onValueChange={(value) =>
                  handleFormChange(value, "organizationName", "managerDto")
                }
                isRequired
                isInvalid={
                  Boolean(organizationNameError) &&
                  !state.formData.managerDto?.organizationName
                }
                errorMessage={organizationNameError}
                className="w-full"
                classNames={inputClassNames}
              />
            )}

            <AppSelect
              name="gender"
              label={t("personalDetails.gender")}
              labelPlacement="outside"
              placeholder={t("personalDetails.genderPlaceholder")}
              selectedKeys={new Set([state.formData.gender || ""])}
              onSelectionChange={(keys) => {
                const selected = Array.from(keys)[0] || "";
                handleFormChange(selected, "gender");
              }}
              isRequired
              isInvalid={Boolean(genderError) && !state.formData.gender}
              errorMessage={genderError}
              className="w-full"
              classNames={selectClassNames}
            >
              <SelectItem key="MALE">{t("personalDetails.male")}</SelectItem>
              <SelectItem key="FEMALE">{t("personalDetails.female")}</SelectItem>
              <SelectItem key="OTHER">{t("personalDetails.other")}</SelectItem>
            </AppSelect>

            {isFighter && (
              <>
                <Input
                  type="text"
                  name="nickname"
                  label={t("personalDetails.nickname")}
                  labelPlacement="outside"
                  placeholder={t("personalDetails.nicknamePlaceholder")}
                  maxLength={25}
                  value={state.formData.fighterDto?.nickname ?? ""}
                  onValueChange={(value) =>
                    handleFormChange(value, "nickname", "fighterDto")
                  }
                  className="w-full"
                  classNames={inputClassNames}
                />

                <DatePicker
                  name="dateOfBirth"
                  label={t("personalDetails.dateOfBirth")}
                  labelPlacement="outside"
                  value={safeParseDate(state.formData.fighterDto?.dateOfBirth)}
                  onChange={(date) =>
                    handleFormChange(
                      date?.toString() ?? "",
                      "dateOfBirth",
                      "fighterDto"
                    )
                  }
                  isRequired
                  isInvalid={!!dobError}
                  errorMessage={dobError}
                  maxValue={today("UTC")}
                  showMonthAndYearPickers
                  className="w-full"
                  classNames={datePickerClassNames}
                />
              </>
            )}
          </div>

          {/* Footer: Back / Next */}
          <div className="flex w-full items-center justify-between gap-3 pt-4 sm:pt-6 [@media(max-height:700px)]:pt-3">
            <Button
              onPress={handleBack}
              variant="flat"
              startContent={<ArrowLeftIcon className="h-4 w-4" />}
              className={tertiaryButtonClass}
            >
              {t("navigation.back")}
            </Button>

            <Button
              type="submit"
              endContent={<ArrowRightIcon className="h-4 w-4" />}
              className="h-10 rounded-2xl lg:rounded-3xl bg-[#0485F7] px-4 text-xs sm:text-sm lg:text-base font-semibold lg:font-medium leading-5 lg:leading-6 text-white hover:bg-[#0374d6] active:scale-[0.99] transition-all shadow-sm disabled:cursor-not-allowed disabled:bg-gray-400"
            >
              {t("navigation.next")}
            </Button>
          </div>
        </form>
      </div>

      {/* Footer */}
      <p className="pt-2 pb-1 text-center text-[11px] sm:text-xs font-normal leading-4 text-[#71717A]">
        {t("footer_copyright", "© Hoowlr 2026, All rights reserved")}
      </p>
    </div>
  </div>

      {/* RIGHT BANNER (hidden below lg) */}
      <div className="hidden shrink-0 p-9 lg:sticky lg:top-0 lg:flex lg:h-dvh lg:w-[45%] xl:w-[55%] 2xl:w-[62%]">
        <div className="flex h-full w-full items-center justify-center overflow-hidden rounded-[2rem] bg-[#1F6BFF]">
          <img
            src={loginBanner}
            alt=""
            className="max-h-full max-w-full object-contain"
          />
        </div>
      </div>
    </div>
  );
};

export default PersonalDetailsStage;